/** Progressive masonry and live section anchors for the Events page. */
class EventsMasonry extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.schedule = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.layout());
    };
    this.observer?.disconnect();
    this.observer = new ResizeObserver(this.schedule);
    this.observer.observe(this);
    this.querySelectorAll('figure').forEach((figure) => this.observer.observe(figure));
    this.addEventListener('load', this.schedule, { capture: true, signal: this.controller.signal });
    this.schedule();
  }

  layout() {
    if (!this.isConnected) return;
    const items = [...this.children];
    if (matchMedia('(max-width: 749px)').matches) {
      this.removeAttribute('data-masonry');
      items.forEach((item) => {
        for (const property of ['grid-row-end', 'grid-row-start', 'grid-column-start']) item.style.removeProperty(property);
      });
      return;
    }
    const style = getComputedStyle(this);
    const gap = parseFloat(style.columnGap) || 0;
    const widths = style.gridTemplateColumns.split(' ').map(Number.parseFloat);
    if (widths.length !== 2 || widths.some((width) => !Number.isFinite(width) || width <= 0)) return;
    const nextRows = [1, 1];
    // Use intrinsic dimensions so lazy images reserve their space before loading.
    // Explicit columns avoid feedback loops as unequal-width columns pack images.
    const positions = items.map((item) => {
      const image = item.querySelector('img');
      const ratio = Number(image?.getAttribute('width')) / Number(image?.getAttribute('height')) || 1;
      const column = nextRows[0] <= nextRows[1] ? 0 : 1;
      const span = Math.max(1, Math.ceil(widths[column] / ratio + gap));
      const row = nextRows[column];
      nextRows[column] += span;
      return { column: column + 1, row, span };
    });
    this.setAttribute('data-masonry', '');
    items.forEach((item, index) => {
      const { column, row, span } = positions[index];
      item.style.gridColumnStart = String(column);
      item.style.gridRowStart = String(row);
      item.style.gridRowEnd = `span ${span}`;
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

class EventsLinks extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.schedule = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateLinks());
    };
    this.observer?.disconnect();
    this.observer = new MutationObserver(this.schedule);
    this.observer.observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
    for (const name of ['DOMContentLoaded', 'shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
      document.addEventListener(name, this.schedule, { signal: this.controller.signal });
    }
    this.schedule();
  }

  updateLinks() {
    if (!this.isConnected) return;
    this.querySelectorAll('[data-events-link]').forEach((link) => {
      const target = document.querySelector(`[data-events-target="${link.dataset.eventsLink}"]`);
      link.hidden = !target;
      if (target) link.setAttribute('href', `#${target.id}`);
      else link.removeAttribute('href');
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('bm-events-masonry')) customElements.define('bm-events-masonry', EventsMasonry);
if (!customElements.get('bm-events-links')) customElements.define('bm-events-links', EventsLinks);
